import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { API, useAuth } from "../App";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "../components/ui/alert-dialog";
import { toast } from "sonner";
import {
  DollarSign,
  CreditCard,
  FileText,
  TrendingUp,
  Loader2,
  Search,
  Eye,
  RefreshCw,
  Download,
  ChevronLeft,
  ExternalLink,
  AlertTriangle,
  CheckCircle,
  Clock,
  XCircle,
  Banknote,
  Receipt,
  Settings,
  Info,
  Building,
} from "lucide-react";

const PaymentsAdmin = () => {
  const { token } = useAuth();
  const [activeTab, setActiveTab] = useState("overview");
  const [loading, setLoading] = useState(true);
  
  // Stats
  const [stats, setStats] = useState(null);
  
  // Transactions
  const [transactions, setTransactions] = useState([]);
  const [transactionsTotal, setTransactionsTotal] = useState(0);
  const [transactionsPage, setTransactionsPage] = useState(1);
  const [transactionSearch, setTransactionSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [typeFilter, setTypeFilter] = useState("all");
  const [loadingTransactions, setLoadingTransactions] = useState(false);
  
  // Invoices
  const [invoices, setInvoices] = useState([]);
  const [invoicesTotal, setInvoicesTotal] = useState(0);
  const [invoicesPage, setInvoicesPage] = useState(1);
  const [loadingInvoices, setLoadingInvoices] = useState(false);
  
  // Stripe config
  const [stripeConfig, setStripeConfig] = useState(null);
  
  // Selected transaction for detail view
  const [selectedTransaction, setSelectedTransaction] = useState(null);

  useEffect(() => {
    fetchStats();
    fetchStripeConfig();
  }, []);

  useEffect(() => {
    if (activeTab === "transactions") {
      fetchTransactions();
    } else if (activeTab === "invoices") {
      fetchInvoices();
    }
  }, [activeTab, transactionsPage, statusFilter, typeFilter, invoicesPage]);

  const fetchStats = async () => {
    try {
      const response = await axios.get(`${API}/admin/payments/stats`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setStats(response.data);
    } catch (error) {
      console.error("Error fetching stats:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchStripeConfig = async () => {
    try {
      const response = await axios.get(`${API}/admin/stripe-config`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setStripeConfig(response.data);
    } catch (error) {
      console.error("Error fetching Stripe config:", error);
    }
  };

  const fetchTransactions = async () => {
    setLoadingTransactions(true);
    try {
      const params = new URLSearchParams();
      params.append("page", transactionsPage.toString());
      params.append("limit", "15");
      if (statusFilter && statusFilter !== "all") params.append("status", statusFilter);
      if (typeFilter && typeFilter !== "all") params.append("item_type", typeFilter);

      const response = await axios.get(`${API}/admin/payments?${params.toString()}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setTransactions(response.data.transactions);
      setTransactionsTotal(response.data.total);
    } catch (error) {
      console.error("Error fetching transactions:", error);
    } finally {
      setLoadingTransactions(false);
    }
  };

  const fetchInvoices = async () => {
    setLoadingInvoices(true);
    try {
      const params = new URLSearchParams();
      params.append("page", invoicesPage.toString());
      params.append("limit", "15");

      const response = await axios.get(`${API}/admin/invoices?${params.toString()}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setInvoices(response.data.invoices);
      setInvoicesTotal(response.data.total);
    } catch (error) {
      console.error("Error fetching invoices:", error);
    } finally {
      setLoadingInvoices(false);
    }
  };

  const handleRefund = async (transactionId) => {
    try {
      await axios.post(`${API}/admin/payments/${transactionId}/refund`, {}, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success("Remboursement effectué");
      fetchTransactions();
      fetchStats();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erreur lors du remboursement");
    }
  };

  const statusColors = {
    paid: "bg-green-500/20 text-green-400 border-green-500/30",
    pending: "bg-yellow-500/20 text-yellow-400 border-yellow-500/30",
    failed: "bg-red-500/20 text-red-400 border-red-500/30",
    refunded: "bg-purple-500/20 text-purple-400 border-purple-500/30",
    expired: "bg-gray-500/20 text-gray-400 border-gray-500/30",
  };

  const statusIcons = {
    paid: <CheckCircle className="w-4 h-4" />,
    pending: <Clock className="w-4 h-4" />,
    failed: <XCircle className="w-4 h-4" />,
    refunded: <RefreshCw className="w-4 h-4" />,
    expired: <AlertTriangle className="w-4 h-4" />,
  };

  const statusLabels = {
    paid: "Payé",
    pending: "En attente",
    failed: "Échoué",
    refunded: "Remboursé",
    expired: "Expiré",
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background" data-testid="payments-admin">
      <Navbar />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-4">
            <Link to="/admin">
              <Button variant="ghost" size="icon">
                <ChevronLeft className="w-5 h-5" />
              </Button>
            </Link>
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 bg-green-500/20 rounded-lg flex items-center justify-center">
                <DollarSign className="w-6 h-6 text-green-400" />
              </div>
              <div>
                <h1 className="text-3xl font-heading font-bold">Paiements & Factures</h1>
                <p className="text-muted-foreground">Gérez vos revenus et transactions</p>
              </div>
            </div>
          </div>
          <div className="flex gap-2">
            {stripeConfig && (
              <a href="https://dashboard.stripe.com" target="_blank" rel="noopener noreferrer">
                <Button variant="outline">
                  <ExternalLink className="w-4 h-4 mr-2" />
                  Dashboard Stripe
                </Button>
              </a>
            )}
            <Button onClick={() => { fetchStats(); fetchTransactions(); fetchInvoices(); }} variant="outline">
              <RefreshCw className="w-4 h-4 mr-2" />
              Actualiser
            </Button>
          </div>
        </div>

        {/* Mode Test Warning */}
        {stripeConfig?.mode === "test" && (
          <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-lg p-4 mb-6 flex items-start gap-3">
            <AlertTriangle className="w-5 h-5 text-yellow-400 mt-0.5" />
            <div>
              <h3 className="font-bold text-yellow-400">Mode Test Actif</h3>
              <p className="text-sm text-muted-foreground">
                Vous utilisez actuellement une clé de test Stripe. Les paiements ne sont pas réels. 
                Configurez votre clé live pour recevoir de vrais paiements.
              </p>
            </div>
          </div>
        )}

        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
          <TabsList className="grid w-full grid-cols-4 lg:w-auto lg:inline-flex">
            <TabsTrigger value="overview" className="flex items-center gap-2">
              <TrendingUp className="w-4 h-4" />
              <span className="hidden sm:inline">Aperçu</span>
            </TabsTrigger>
            <TabsTrigger value="transactions" className="flex items-center gap-2">
              <CreditCard className="w-4 h-4" />
              <span className="hidden sm:inline">Transactions</span>
            </TabsTrigger>
            <TabsTrigger value="invoices" className="flex items-center gap-2">
              <FileText className="w-4 h-4" />
              <span className="hidden sm:inline">Factures</span>
            </TabsTrigger>
            <TabsTrigger value="settings" className="flex items-center gap-2">
              <Settings className="w-4 h-4" />
              <span className="hidden sm:inline">Configuration</span>
            </TabsTrigger>
          </TabsList>

          {/* Overview Tab */}
          <TabsContent value="overview" className="space-y-6">
            {/* Stats Cards */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
              <div className="bg-card border border-border rounded-lg p-6">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-green-500/20 rounded-lg flex items-center justify-center">
                    <DollarSign className="w-6 h-6 text-green-400" />
                  </div>
                  <div>
                    <p className="text-3xl font-heading font-bold">
                      ${stats?.total_revenue?.toFixed(2) || "0.00"}
                    </p>
                    <p className="text-sm text-muted-foreground">Revenus totaux</p>
                  </div>
                </div>
              </div>

              <div className="bg-card border border-border rounded-lg p-6">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-blue-500/20 rounded-lg flex items-center justify-center">
                    <CreditCard className="w-6 h-6 text-blue-400" />
                  </div>
                  <div>
                    <p className="text-3xl font-heading font-bold">
                      {stats?.total_transactions || 0}
                    </p>
                    <p className="text-sm text-muted-foreground">Transactions</p>
                  </div>
                </div>
              </div>

              <div className="bg-card border border-border rounded-lg p-6">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-purple-500/20 rounded-lg flex items-center justify-center">
                    <TrendingUp className="w-6 h-6 text-purple-400" />
                  </div>
                  <div>
                    <p className="text-3xl font-heading font-bold">
                      ${stats?.recent_30_days?.revenue?.toFixed(2) || "0.00"}
                    </p>
                    <p className="text-sm text-muted-foreground">30 derniers jours</p>
                  </div>
                </div>
              </div>

              <div className="bg-card border border-border rounded-lg p-6">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-yellow-500/20 rounded-lg flex items-center justify-center">
                    <Clock className="w-6 h-6 text-yellow-400" />
                  </div>
                  <div>
                    <p className="text-3xl font-heading font-bold">
                      {stats?.pending_payments || 0}
                    </p>
                    <p className="text-sm text-muted-foreground">En attente</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Revenue by Type */}
            <div className="grid lg:grid-cols-2 gap-6">
              <div className="bg-card border border-border rounded-lg p-6">
                <h3 className="font-heading font-bold mb-4 flex items-center gap-2">
                  <Receipt className="w-5 h-5 text-primary" />
                  Revenus par type
                </h3>
                <div className="space-y-4">
                  {stats?.revenue_by_type && Object.entries(stats.revenue_by_type).map(([type, data]) => (
                    <div key={type} className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        {type === "course" ? (
                          <Badge className="bg-primary/20 text-primary">Cours</Badge>
                        ) : (
                          <Badge className="bg-secondary/20 text-secondary">Abonnements</Badge>
                        )}
                        <span className="text-sm text-muted-foreground">
                          ({data.count} transactions)
                        </span>
                      </div>
                      <span className="font-bold text-lg">${data.total.toFixed(2)}</span>
                    </div>
                  ))}
                  {(!stats?.revenue_by_type || Object.keys(stats.revenue_by_type).length === 0) && (
                    <p className="text-muted-foreground text-center py-4">Aucune donnée</p>
                  )}
                </div>
              </div>

              <div className="bg-card border border-border rounded-lg p-6">
                <h3 className="font-heading font-bold mb-4 flex items-center gap-2">
                  <TrendingUp className="w-5 h-5 text-primary" />
                  Historique mensuel
                </h3>
                <div className="space-y-3 max-h-64 overflow-y-auto">
                  {stats?.monthly_revenue?.map((month) => (
                    <div key={month._id} className="flex items-center justify-between py-2 border-b border-border last:border-0">
                      <div>
                        <p className="font-medium">{month._id}</p>
                        <p className="text-xs text-muted-foreground">{month.count} transactions</p>
                      </div>
                      <span className="font-bold text-green-400">${month.total.toFixed(2)}</span>
                    </div>
                  ))}
                  {(!stats?.monthly_revenue || stats.monthly_revenue.length === 0) && (
                    <p className="text-muted-foreground text-center py-4">Aucune donnée</p>
                  )}
                </div>
              </div>
            </div>
          </TabsContent>

          {/* Transactions Tab */}
          <TabsContent value="transactions" className="space-y-4">
            {/* Filters */}
            <div className="flex flex-col sm:flex-row gap-4">
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger className="w-full sm:w-40">
                  <SelectValue placeholder="Statut" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tous les statuts</SelectItem>
                  <SelectItem value="paid">Payé</SelectItem>
                  <SelectItem value="pending">En attente</SelectItem>
                  <SelectItem value="failed">Échoué</SelectItem>
                  <SelectItem value="refunded">Remboursé</SelectItem>
                </SelectContent>
              </Select>

              <Select value={typeFilter} onValueChange={setTypeFilter}>
                <SelectTrigger className="w-full sm:w-40">
                  <SelectValue placeholder="Type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tous les types</SelectItem>
                  <SelectItem value="course">Cours</SelectItem>
                  <SelectItem value="subscription">Abonnement</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Transactions Table */}
            <div className="bg-card border border-border rounded-lg overflow-hidden">
              {loadingTransactions ? (
                <div className="p-12 text-center">
                  <Loader2 className="w-8 h-8 animate-spin text-primary mx-auto" />
                </div>
              ) : transactions.length === 0 ? (
                <div className="p-12 text-center">
                  <CreditCard className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                  <p className="text-muted-foreground">Aucune transaction trouvée</p>
                </div>
              ) : (
                <>
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead className="bg-muted/50 border-b border-border">
                        <tr>
                          <th className="text-left p-4 font-medium text-sm">ID</th>
                          <th className="text-left p-4 font-medium text-sm">Client</th>
                          <th className="text-left p-4 font-medium text-sm">Article</th>
                          <th className="text-left p-4 font-medium text-sm">Montant</th>
                          <th className="text-left p-4 font-medium text-sm">Statut</th>
                          <th className="text-left p-4 font-medium text-sm">Date</th>
                          <th className="text-right p-4 font-medium text-sm">Actions</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-border">
                        {transactions.map((txn) => (
                          <tr key={txn.transaction_id} className="hover:bg-muted/30">
                            <td className="p-4 font-mono text-xs">
                              {txn.transaction_id.slice(0, 15)}...
                            </td>
                            <td className="p-4">
                              <div>
                                <p className="font-medium">{txn.user?.name || "N/A"}</p>
                                <p className="text-xs text-muted-foreground">{txn.user?.email}</p>
                              </div>
                            </td>
                            <td className="p-4">
                              <div className="flex items-center gap-2">
                                <Badge variant="outline" className="text-xs">
                                  {txn.item_type === "course" ? "Cours" : "Abo"}
                                </Badge>
                                <span className="text-sm truncate max-w-[150px]">{txn.item_name}</span>
                              </div>
                            </td>
                            <td className="p-4 font-bold">${txn.amount.toFixed(2)}</td>
                            <td className="p-4">
                              <Badge className={statusColors[txn.payment_status]}>
                                {statusIcons[txn.payment_status]}
                                <span className="ml-1">{statusLabels[txn.payment_status]}</span>
                              </Badge>
                            </td>
                            <td className="p-4 text-sm text-muted-foreground">
                              {new Date(txn.created_at).toLocaleDateString("fr-FR")}
                            </td>
                            <td className="p-4">
                              <div className="flex justify-end gap-2">
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  onClick={() => setSelectedTransaction(txn)}
                                >
                                  <Eye className="w-4 h-4" />
                                </Button>
                                {txn.payment_status === "paid" && (
                                  <AlertDialog>
                                    <AlertDialogTrigger asChild>
                                      <Button variant="ghost" size="icon" className="text-destructive">
                                        <RefreshCw className="w-4 h-4" />
                                      </Button>
                                    </AlertDialogTrigger>
                                    <AlertDialogContent>
                                      <AlertDialogHeader>
                                        <AlertDialogTitle>Rembourser ce paiement?</AlertDialogTitle>
                                        <AlertDialogDescription>
                                          Cette action annulera l'accès au cours/abonnement pour le client.
                                          Vous devrez effectuer le remboursement manuellement dans Stripe.
                                        </AlertDialogDescription>
                                      </AlertDialogHeader>
                                      <AlertDialogFooter>
                                        <AlertDialogCancel>Annuler</AlertDialogCancel>
                                        <AlertDialogAction
                                          onClick={() => handleRefund(txn.transaction_id)}
                                          className="bg-destructive"
                                        >
                                          Rembourser
                                        </AlertDialogAction>
                                      </AlertDialogFooter>
                                    </AlertDialogContent>
                                  </AlertDialog>
                                )}
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>

                  {/* Pagination */}
                  <div className="p-4 border-t border-border flex items-center justify-between">
                    <p className="text-sm text-muted-foreground">
                      {transactionsTotal} transaction(s) au total
                    </p>
                    <div className="flex gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        disabled={transactionsPage === 1}
                        onClick={() => setTransactionsPage(p => p - 1)}
                      >
                        Précédent
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        disabled={transactions.length < 15}
                        onClick={() => setTransactionsPage(p => p + 1)}
                      >
                        Suivant
                      </Button>
                    </div>
                  </div>
                </>
              )}
            </div>
          </TabsContent>

          {/* Invoices Tab */}
          <TabsContent value="invoices" className="space-y-4">
            <div className="bg-card border border-border rounded-lg overflow-hidden">
              {loadingInvoices ? (
                <div className="p-12 text-center">
                  <Loader2 className="w-8 h-8 animate-spin text-primary mx-auto" />
                </div>
              ) : invoices.length === 0 ? (
                <div className="p-12 text-center">
                  <FileText className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                  <p className="text-muted-foreground">Aucune facture</p>
                </div>
              ) : (
                <>
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead className="bg-muted/50 border-b border-border">
                        <tr>
                          <th className="text-left p-4 font-medium text-sm">N° Facture</th>
                          <th className="text-left p-4 font-medium text-sm">Client</th>
                          <th className="text-left p-4 font-medium text-sm">Article</th>
                          <th className="text-left p-4 font-medium text-sm">Montant</th>
                          <th className="text-left p-4 font-medium text-sm">Date</th>
                          <th className="text-right p-4 font-medium text-sm">Actions</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-border">
                        {invoices.map((inv) => (
                          <tr key={inv.transaction_id} className="hover:bg-muted/30">
                            <td className="p-4 font-mono font-bold text-primary">
                              {inv.invoice_number}
                            </td>
                            <td className="p-4">
                              <div>
                                <p className="font-medium">{inv.user?.name || "N/A"}</p>
                                <p className="text-xs text-muted-foreground">{inv.user?.email}</p>
                              </div>
                            </td>
                            <td className="p-4">
                              <span className="text-sm">{inv.item_name}</span>
                            </td>
                            <td className="p-4 font-bold">${inv.amount.toFixed(2)}</td>
                            <td className="p-4 text-sm text-muted-foreground">
                              {new Date(inv.created_at).toLocaleDateString("fr-FR")}
                            </td>
                            <td className="p-4">
                              <div className="flex justify-end">
                                <Button variant="outline" size="sm">
                                  <Download className="w-4 h-4 mr-1" />
                                  PDF
                                </Button>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>

                  {/* Pagination */}
                  <div className="p-4 border-t border-border flex items-center justify-between">
                    <p className="text-sm text-muted-foreground">
                      {invoicesTotal} facture(s) au total
                    </p>
                    <div className="flex gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        disabled={invoicesPage === 1}
                        onClick={() => setInvoicesPage(p => p - 1)}
                      >
                        Précédent
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        disabled={invoices.length < 15}
                        onClick={() => setInvoicesPage(p => p + 1)}
                      >
                        Suivant
                      </Button>
                    </div>
                  </div>
                </>
              )}
            </div>
          </TabsContent>

          {/* Settings Tab - Stripe Configuration */}
          <TabsContent value="settings" className="space-y-6">
            <div className="bg-card border border-border rounded-lg p-6">
              <h2 className="font-heading font-bold text-xl mb-6 flex items-center gap-2">
                <Building className="w-6 h-6 text-primary" />
                Configuration Stripe - Recevoir vos paiements
              </h2>

              <div className="space-y-6">
                {/* Current Status */}
                <div className="bg-muted/30 rounded-lg p-4">
                  <div className="flex items-center gap-3 mb-2">
                    <Info className="w-5 h-5 text-primary" />
                    <span className="font-bold">Statut actuel:</span>
                    <Badge className={stripeConfig?.mode === "test" ? "bg-yellow-500/20 text-yellow-400" : "bg-green-500/20 text-green-400"}>
                      Mode {stripeConfig?.mode === "test" ? "Test" : "Production"}
                    </Badge>
                  </div>
                  <p className="text-sm text-muted-foreground">
                    Clé actuelle: <code className="bg-muted px-2 py-0.5 rounded">{stripeConfig?.current_key_prefix}</code>
                  </p>
                </div>

                {/* Setup Instructions */}
                <div>
                  <h3 className="font-bold mb-4 flex items-center gap-2">
                    <Banknote className="w-5 h-5" />
                    Comment recevoir l'argent sur votre compte bancaire (RIB)
                  </h3>
                  
                  <div className="space-y-4">
                    {stripeConfig?.setup_instructions && Object.entries(stripeConfig.setup_instructions).map(([step, instruction], index) => (
                      <div key={step} className="flex gap-4">
                        <div className="w-8 h-8 rounded-full bg-primary/20 flex items-center justify-center shrink-0">
                          <span className="font-bold text-primary">{index + 1}</span>
                        </div>
                        <div className="flex-1">
                          <p className="text-sm">{instruction}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Stripe Dashboard Links */}
                <div className="border-t border-border pt-6">
                  <h3 className="font-bold mb-4">Liens utiles Stripe Dashboard</h3>
                  <div className="grid sm:grid-cols-2 gap-4">
                    {stripeConfig?.stripe_dashboard_urls && Object.entries(stripeConfig.stripe_dashboard_urls).map(([key, url]) => (
                      <a
                        key={key}
                        href={url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center gap-3 p-4 bg-muted/30 rounded-lg hover:bg-muted/50 transition-colors"
                      >
                        <ExternalLink className="w-4 h-4 text-primary" />
                        <div>
                          <p className="font-medium capitalize">{key.replace("_", " ")}</p>
                          <p className="text-xs text-muted-foreground">Ouvrir dans Stripe</p>
                        </div>
                      </a>
                    ))}
                  </div>
                </div>

                {/* Important Info */}
                <div className="bg-primary/10 border border-primary/30 rounded-lg p-4">
                  <h4 className="font-bold text-primary mb-2">📌 Informations importantes</h4>
                  <ul className="text-sm space-y-2 text-muted-foreground">
                    <li>• <strong>Délai de virement:</strong> Stripe effectue les virements automatiquement tous les jours (J+2 minimum)</li>
                    <li>• <strong>Frais Stripe:</strong> 1.4% + 0.25€ par transaction en Europe</li>
                    <li>• <strong>Devise:</strong> Les paiements sont en USD, convertis en EUR lors du virement</li>
                    <li>• <strong>Factures:</strong> Stripe génère automatiquement les reçus pour vos clients</li>
                    <li>• <strong>Remboursements:</strong> Effectuez-les directement depuis le dashboard Stripe</li>
                  </ul>
                </div>
              </div>
            </div>
          </TabsContent>
        </Tabs>
      </div>

      {/* Transaction Detail Modal */}
      <Dialog open={!!selectedTransaction} onOpenChange={() => setSelectedTransaction(null)}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Détails de la transaction</DialogTitle>
          </DialogHeader>
          {selectedTransaction && (
            <div className="space-y-4 py-4">
              <div className="flex justify-between items-center">
                <span className="text-muted-foreground">ID Transaction:</span>
                <code className="bg-muted px-2 py-1 rounded text-xs">
                  {selectedTransaction.transaction_id}
                </code>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-muted-foreground">Statut:</span>
                <Badge className={statusColors[selectedTransaction.payment_status]}>
                  {statusLabels[selectedTransaction.payment_status]}
                </Badge>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-muted-foreground">Montant:</span>
                <span className="font-bold text-lg">${selectedTransaction.amount.toFixed(2)}</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-muted-foreground">Type:</span>
                <span>{selectedTransaction.item_type === "course" ? "Cours" : "Abonnement"}</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-muted-foreground">Article:</span>
                <span className="text-right max-w-[200px] truncate">{selectedTransaction.item_name}</span>
              </div>
              <div className="border-t border-border pt-4">
                <p className="text-muted-foreground mb-2">Client:</p>
                <div className="bg-muted/30 rounded-lg p-3">
                  <p className="font-medium">{selectedTransaction.user?.name}</p>
                  <p className="text-sm text-muted-foreground">{selectedTransaction.user?.email}</p>
                </div>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-muted-foreground">Date:</span>
                <span>{new Date(selectedTransaction.created_at).toLocaleString("fr-FR")}</span>
              </div>
              {selectedTransaction.session_id && (
                <div className="flex justify-between items-center">
                  <span className="text-muted-foreground">Session Stripe:</span>
                  <code className="bg-muted px-2 py-1 rounded text-xs truncate max-w-[200px]">
                    {selectedTransaction.session_id}
                  </code>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      <Footer />
    </div>
  );
};

export default PaymentsAdmin;
